// ApisServiceDesignDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored
// Fix: ESLint "id is not defined" happens when JSX accidentally treats `id` as a JS variable.
// I made all “(created_at, id)” / placeholder tokens explicit strings (no accidental {id} expressions).

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small UI helpers ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <h2 className="h4 mb-2">{title}</h2>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

function BadgeRight({ children, tone = "secondary" }) {
  return (
    <span className={`badge bg-${tone} text-white`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function LabeledCard({ label, tone, title, children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone={tone}>{label}</BadgeRight>
        </div>
        <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const TemplateCard = ({ title = "Template", children }) => (
  <LabeledCard label="Template" tone="secondary" title={title}>
    {children}
  </LabeledCard>
);

const ExampleCard = ({ title = "Facebook-like Home Feed — example", children }) => (
  <LabeledCard label="Example" tone="success" title={title}>
    {children}
  </LabeledCard>
);

function CodeCard({ title = "Template — code / shape", children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone="secondary">Template</BadgeRight>
        </div>
        <pre
          className="bg-light rounded p-3 border small mb-0"
          style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
        >
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );
}

function SayCallout({ children }) {
  return (
    <div className="alert alert-primary d-flex align-items-center mb-3" role="alert">
      <div className="me-2 fw-semibold">Say</div>
      <div className="mb-0">{children}</div>
    </div>
  );
}

/* ================== TOC ================== */
const TOC = [
  { id: "protocols", label: "A) REST vs gRPC vs GraphQL" },
  { id: "gateway", label: "B) API gateway & BFF patterns" },
  { id: "resource-model", label: "C) Resource modeling & standards" },
  { id: "pagination-realtime", label: "D) Pagination, filtering & realtime (SSE/WS)" },
  { id: "versioning", label: "E) Versioning & compatibility" },
  { id: "idempotency", label: "F) Idempotency & exactly-once effects" },
  { id: "auth", label: "G) AuthN/AuthZ, security & abuse" },
  { id: "multitenancy", label: "H) Multi-tenancy, quotas & isolation" },
  { id: "errors", label: "I) Error model & API ergonomics" },
  { id: "observability", label: "J) Observability for APIs" },
  { id: "rollouts-safety", label: "K) Rollouts & safety" },
  { id: "one-liners", label: "Interview one-liners" },
];

/* ================== page ================== */
export default function ApisServiceDesignDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id: sectionId }) => {
      const el = document.getElementById(sectionId);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  const feed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      baseUrl: "/v1",
      endpoints: {
        getFeed: "GET /v1/feed",
        getFeedPage: "GET /v1/feed?limit=25&cursor=...",
        // NOTE: keep placeholders inside strings; never write `{id}` directly in JSX text.
        like: "POST /v1/posts/{post_id}/likes",
        follow: "POST /v1/users/{user_id}/follows",
        createPost: "POST /v1/posts",
      },
      realtime: ["SSE: new posts & notifications", "WebSocket: chat/presence (optional)"],
      slo: { availability: "99.9% monthly", latencyP95: "≤ 200ms for GET /feed" },
    }),
    []
  );

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>APIs &amp; service design</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(111,66,193,.07), rgba(13,110,253,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">10) APIs &amp; service design — detailed guide</h1>
          <p className="text-muted mb-0">
            Protocol choices, gateway patterns, pagination/real-time, versioning, idempotency,
            multi-tenancy, security, and operational guardrails — with a {feed.name} example in every section.
          </p>
        </div>
      </header>

      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h3 className="h6 text-uppercase text-muted mb-2">On this page</h3>
                <nav className="nav flex-column small">
                  {TOC.map(({ id: sectionId, label }) => (
                    <a
                      key={sectionId}
                      href={`#${sectionId}`}
                      className={`nav-link px-0 ${activeId === sectionId ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === sectionId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">
              Tip: Start API design from access patterns + SLOs + error model.
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* A) Protocols */}
          <Section id="protocols" title="A) REST vs gRPC vs GraphQL (how to choose)">
            <SayCallout>“I pick protocol based on clients, latency needs, and caching—not popularity.”</SayCallout>

            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">REST (HTTP/JSON)</div>
                    <ul className="mb-0 small text-muted">
                      <li>Best for public/partner APIs</li>
                      <li>Human-debuggable, CDN/proxy friendly</li>
                      <li>HTTP caching + ETags</li>
                      <li>Pitfalls: N+1 chatter, inconsistent errors</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">gRPC (HTTP/2 + Protobuf)</div>
                    <ul className="mb-0 small text-muted">
                      <li>Best for service-to-service</li>
                      <li>Low latency, compact payloads</li>
                      <li>Streaming (client/server/bidi)</li>
                      <li>Pitfalls: browsers need proxy, caching awkward</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">GraphQL</div>
                    <ul className="mb-0 small text-muted">
                      <li>Best for complex product UIs</li>
                      <li>Solves over/under-fetching</li>
                      <li>Strong schema + typed fields</li>
                      <li>Pitfalls: expensive queries, caching/resolvers N+1</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <TemplateCard title="Protocol choice — template">
{`• REST: external APIs, cacheable reads, heterogeneous clients, “resource” semantics.
• gRPC: internal microservices, low latency, strict contracts, streaming.
• GraphQL: UI composition across many domains, rapid frontend iteration.

Guardrails:
• GraphQL: persisted queries, depth/complexity limits, batching (dataloaders).
• gRPC: timeouts, retries with budgets, per-method SLOs.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — protocol choices`}>
{`• Public/mobile API: REST for /feed and /posts (easy debugging + CDN).
• Internal: gRPC between Feed Service ↔ Ranking ↔ Graph (low latency).
• Optional: GraphQL BFF for web UI that composes feed + profile + ads with flexible fields.`}
            </ExampleCard>
          </Section>

          {/* In-article ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}

          {/* B) Gateway */}
          <Section id="gateway" title="B) API gateway & BFF patterns (edge, auth, shaping)">
            <SayCallout>“The gateway enforces cross-cutting concerns; services own business logic.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Gateway responsibilities</h3>
            <ul className="mb-0">
              <li><strong>TLS termination</strong>, routing, request normalization, compression.</li>
              <li><strong>AuthN/AuthZ</strong>, token validation, scopes, mTLS (internal).</li>
              <li><strong>Rate limiting</strong>, quotas, WAF, bot protection.</li>
              <li><strong>Response shaping</strong>: caching, header policies, partial responses.</li>
              <li><strong>Protocol translation</strong>: REST ↔ gRPC (when needed).</li>
            </ul>

            <TemplateCard title="Gateway vs BFF — template">
{`• Edge Gateway: security + traffic controls (auth, rate limits, WAF), routing, caching.
• BFF: client-specific aggregation, tailoring payloads, reducing round trips.
• Keep business logic in services; BFF should be thin (composition + formatting).`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — edge + BFF`}>
{`• Gateway validates JWT, applies per-user quotas, and caches GET /feed for 2–5 seconds.
• Web BFF composes feed items with profile snippets and ad metadata for the web UI.
• Internal services talk via gRPC; BFF uses timeouts + fallbacks if a dependency is slow.`}
            </ExampleCard>
          </Section>

          {/* C) Resource model */}
          <Section id="resource-model" title="C) Resource modeling & standards (naming, status codes, caching)">
            <SayCallout>“Consistent resource shapes reduce bugs more than any framework choice.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Basics</h3>
            <ul className="mb-0">
              <li>Prefer <strong>nouns</strong>: <code>/posts</code>, <code>/users</code>, <code>/feed</code>.</li>
              <li>Use verbs via HTTP methods: GET/POST/PUT/PATCH/DELETE.</li>
              <li>Return correct status codes: 200/201/204, 400/401/403/404/409/429, 5xx.</li>
              <li>Use caching for reads: ETag + If-None-Match; short TTL caches at edge (where safe).</li>
            </ul>

            <CodeCard title="REST shape — template">
{`GET  /v1/posts/{post_id}
→ 200
{
  "id": "p_123",
  "author_id": "u_7",
  "created_at": "2026-01-10T00:00:00Z",
  "text": "...",
  "stats": { "likes": 10, "comments": 2 }
}

PATCH /v1/posts/{post_id}
If-Match: "etag_abc"
{ "text": "updated" }
→ 200 (or 409 if ETag mismatch)`}
            </CodeCard>

            <ExampleCard title={`${feed.name} — endpoint inventory`}>
{`${feed.endpoints.getFeed}
${feed.endpoints.createPost}
${feed.endpoints.like}
${feed.endpoints.follow}

Notes:
• GET /feed is cacheable (short TTL + ETag).
• Writes use idempotency keys and clear 409/429 responses.`}
            </ExampleCard>
          </Section>

          {/* D) Pagination & realtime */}
          <Section id="pagination-realtime" title="D) Pagination, filtering & realtime (SSE/WebSocket/LP)">
            <SayCallout>“Use cursor pagination for scale; SSE for server→client updates; WebSocket for bi-directional.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Pagination</h3>
            <ul className="mb-0">
              <li><strong>Offset/limit:</strong> simple but slow/unstable at large offsets.</li>
              <li>
                <strong>Cursor:</strong> stable sort keys like{" "}
                <code>{"(created_at, id)"}</code> (explicit string to avoid ESLint no-undef).
              </li>
              <li><strong>Rules:</strong> bounded limit, stable ordering, return next_cursor, avoid heavy total counts.</li>
            </ul>

            <CodeCard title="Cursor pagination — template">
{`GET /v1/feed?limit=25&cursor=eyJjcmVhdGVkX2F0IjoiMjAyNi0wMS0xMCIsImlkIjoicF8xMjMifQ==
→ 200
{
  "items": [...],
  "next_cursor": "eyJjcmVhdGVkX2F0IjoiMjAyNi0wMS0wOSIsImlkIjoicF85ODcifQ=="
}`}
            </CodeCard>

            <h3 className="h6 text-uppercase text-muted mt-3">Realtime</h3>
            <ul className="mb-0">
              <li><strong>Long polling:</strong> simplest fallback, more overhead.</li>
              <li><strong>SSE:</strong> server→client stream over HTTP; easy reconnect; great for feed updates.</li>
              <li><strong>WebSocket:</strong> full duplex; best for chat/presence; needs backpressure + heartbeat.</li>
            </ul>

            <ExampleCard title={`${feed.name} — pagination + realtime`}>
{`• Feed scrolling uses cursor pagination (created_at + post_id).
• Feed updates use SSE:
  - client connects to /v1/feed/stream
  - server pushes “new_post”
  - client merges into UI and can re-fetch with cursor if needed.`}
            </ExampleCard>
          </Section>

          {/* E) Versioning */}
          <Section id="versioning" title="E) Versioning & compatibility (expand → dual-run → contract)">
            <SayCallout>“Default to additive changes; breaking changes ship behind a new version.”</SayCallout>

            <TemplateCard title="Compatibility — template">
{`• Expand: add new fields/endpoints alongside old.
• Dual-run: write/read both shapes where needed, compare results.
• Contract: remove old after deprecation window and adoption is high.
• Measure: traffic by version + error rates + client adoption before removal.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — versioning example`}>
{`• v1: GET /feed returns basic items.
• v2: adds “reason” fields (“why you saw this post”) and new ranking metadata.
• Sunset: warn in response headers, track v1 traffic, then deprecate.`}
            </ExampleCard>
          </Section>

          {/* F) Idempotency */}
          <Section id="idempotency" title="F) Idempotency & exactly-once effects (practical)">
            <SayCallout>“We can’t guarantee exactly-once delivery, but we can guarantee exactly-once effects.”</SayCallout>

            <CodeCard title="Idempotency key — template">
{`POST /v1/posts
Idempotency-Key: 8f1a1c9a-...
{ "post_id": "p_123", "text": "hello" }

Server behavior:
• First request: create record + store response keyed by (tenant, key, route)
• Retry: return same response (200/201) without re-creating`}
            </CodeCard>

            <ExampleCard title={`${feed.name} — idempotent writes`}>
{`• POST /posts uses Idempotency-Key to avoid double post creation.
• POST /likes uses (user_id, post_id) unique constraint to prevent duplicate likes.
• Fanout events use outbox; consumers are idempotent (dedupe by event_id).`}
            </ExampleCard>
          </Section>

          {/* G) Auth */}
          <Section id="auth" title="G) AuthN/AuthZ, security & abuse controls">
            <SayCallout>“Auth belongs at the edge AND in the service—defense in depth.”</SayCallout>

            <ul className="mb-0">
              <li><strong>AuthN:</strong> JWT/OAuth2; validate issuer/audience/expiry; short TTL access tokens.</li>
              <li><strong>AuthZ:</strong> scopes + resource checks (owner/relationship/privacy). Enforce again in service.</li>
              <li><strong>Abuse:</strong> WAF + bot controls; rate limits per user/app/IP; anomaly detection.</li>
              <li><strong>Privacy:</strong> least privilege; audit logs for sensitive operations.</li>
            </ul>

            <ExampleCard title={`${feed.name} — privacy & auth`}>
{`• GET /feed enforces privacy: blocks, private accounts, content restrictions.
• Gateway validates JWT; Feed service re-checks resource-level authorization.
• Write endpoints are more strictly rate-limited than reads.`}
            </ExampleCard>
          </Section>

          {/* H) Multi-tenancy */}
          <Section id="multitenancy" title="H) Multi-tenancy, quotas & isolation">
            <SayCallout>“Multi-tenancy is a reliability problem: quotas, bulkheads, tenant-aware caching.”</SayCallout>

            <ul className="mb-0">
              <li><strong>Isolation:</strong> shared-everything (tenant_id), isolated DB, or dedicated stack.</li>
              <li><strong>Noisy neighbor:</strong> per-tenant quotas, concurrency caps, priority queues.</li>
              <li><strong>Tenant-safe caches:</strong> tenant_id in keys; no cross-tenant leakage.</li>
            </ul>

            <ExampleCard title={`${feed.name} — tenant controls`}>
{`• Per-tenant quotas at gateway + service.
• Per-tenant concurrency caps prevent one tenant spike from exhausting DB pools.
• Cache keys include tenant_id + user_id + privacy policy version.`}
            </ExampleCard>
          </Section>

          {/* I) Errors */}
          <Section id="errors" title="I) Error model & API ergonomics">
            <SayCallout>“A consistent error shape is an ops tool: faster debugging and safer retries.”</SayCallout>

            <CodeCard title="Error response — template">
{`{
  "error": {
    "code": "invalid_argument",
    "message": "limit must be between 1 and 50",
    "details": [{ "field": "limit", "reason": "out_of_range", "min": 1, "max": 50 }]
  },
  "request_id": "req_abc123"
}`}
            </CodeCard>

            <ExampleCard title={`${feed.name} — client-friendly behaviors`}>
{`• 429 responses include Retry-After + rate limit headers.
• Errors always include request_id for support correlation.
• Write endpoints accept Idempotency-Key to make retries safe.`}
            </ExampleCard>
          </Section>

          {/* J) Observability */}
          <Section id="observability" title="J) Observability for APIs">
            <SayCallout>“RED metrics per route + exemplars to traces = fast diagnosis.”</SayCallout>

            <ul className="mb-0">
              <li><strong>Metrics:</strong> RED per route (RPS, error rate, latency histograms), sizes.</li>
              <li><strong>Tracing:</strong> propagate TraceContext end-to-end (sync + async).</li>
              <li><strong>Logs:</strong> structured, include request_id, route template, tenant_id (no PII).</li>
              <li><strong>Alerts:</strong> page on SLO burn; ticket on dependency saturation.</li>
            </ul>

            <ExampleCard title={`${feed.name} — SLOs`}>
{`• SLO: GET /feed p95 ≤ 200ms, availability 99.9% monthly.
• Paging: multi-window burn-rate alerts (fast + slow).
• Drilldown: metric exemplar → trace → logs by request_id.`}
            </ExampleCard>
          </Section>

          {/* K) Rollouts */}
          <Section id="rollouts-safety" title="K) Rollouts & safety">
            <SayCallout>“Every API change ships with rollback paths and guardrails.”</SayCallout>

            <ul className="mb-0">
              <li>Canary 1%→5%→25%→100% gated on latency/error/burn deltas.</li>
              <li>Feature flags for fast bailout without redeploy.</li>
              <li>Schema compatibility tests in CI.</li>
            </ul>

            <ExampleCard title={`${feed.name} — shipping a new ranking feature`}>
{`• Gate canary on p95 + error deltas.
• If slow: flag off enrichment and serve cached feed.
• Rollback: gateway routes traffic back to previous version immediately.`}
            </ExampleCard>
          </Section>

          {/* One-liners */}
          <Section id="one-liners" title="Interview one-liners">
            <ul className="mb-0">
              <li>“REST at the edge, gRPC inside; GraphQL for UI composition.”</li>
              <li>“Cursor pagination with stable sort keys; bounded limits.”</li>
              <li>“Expand → dual-run → contract for safe versioning.”</li>
              <li>“Idempotency keys + outbox + UPSERT gives exactly-once effects.”</li>
              <li>“SLO-burn paging, exemplars for drill-down, canary rollouts with guardrails.”</li>
            </ul>
          </Section>

          {/* Optional bottom ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
